/**
 *
 * Copyright (C) SiteVision AB 2002-2025, all rights reserved
 *
 * @author Henrik Ekelöf
 *
 * SiteVision Responsive Tables
 */
import $ from '@sv/jquery';

const baseClassName = 'sv-responsiveTable';
const responsiveTables = {};
responsiveTables.classNames = {};
responsiveTables.selectors = {};

$.each(
  [
    ['groupName', ''],
    ['scroll', '--scroll'],
    ['fixedFirst', '--fixedFirst'],
    ['stackGroupName', '--stacked'],
    ['stackTable', '--stackTable'],
    ['stackColumns', '--stackColumns'],
  ],
  function (i, t) {
    responsiveTables.classNames[t[0]] = baseClassName + t[1];
    responsiveTables.selectors[t[0]] = '.' + baseClassName + t[1];
  }
);

responsiveTables.config = {
  breakpoint: '767px',
};

const divClassTmpl = ({ classNames }) => `<div class="${classNames}" />`;
const divStyleTmpl = ({ classNames, styleAttr }) =>
  `<div class="${classNames}" style="${styleAttr}" />`;
const tblTmpl = ({ classNames }) =>
  `<table class="${classNames}"><tbody></tbody></table>`;
const tblThRowTmpl = ({ trClassNames, thClassNames, colSpan, content }) =>
  `<tr class="${trClassNames}"><th class="${thClassNames}" colspan="${colSpan}">${content}</th></tr>`;
const tblTrRowTmpl = ({
  trClassNames,
  groupName,
  keyContent,
  cellClassName,
  valueContent,
}) =>
  `<tr class="${trClassNames}"><td class="${groupName}__key">${keyContent}</td><td class="${groupName}__value ${cellClassName}">${valueContent}</td></tr>`;

/**
 * SiteVision Responsive Tables
 */

const classNames = responsiveTables.classNames;
const selectors = responsiveTables.selectors;

(function () {
  /**
   * Simple Scrolling Responsive Table
   */

  if (!$.svRespTables) {
    $.svRespTables = {};
  }

  $.svRespTables.scroll = function (el) {
    this.init = () => {
      $(el)
        .removeClass(classNames.groupName)
        .wrap(
          divClassTmpl({
            classNames: classNames.groupName,
          })
        );
    };
    this.init();
  };

  $.fn.svRespTables_scroll = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.scroll(this);
      /*jshint nonew:true*/
    });
  };

  /**
   * ZURB Studios Responsive Table
   * (Fixed first column)
   *
   * http://zurb.com/playground/responsive-tables
   *
   * MIT License
   *
   * modified by Henrik Ekelöf
   */

  $.svRespTables.fixedFirst = function (el) {
    var base = this,
      mql = responsiveTables.mql;

    const fullClassName = classNames.groupName + ' ' + classNames.fixedFirst;
    let switched = false;

    base.$el = $(el);

    base.$el.data('svRespTables.fixedFirst', base);

    base.init = function () {
      base.$el.removeClass(fullClassName).wrap(
        divClassTmpl({
          classNames: fullClassName,
        })
      );
    };

    base.splitTable = function () {
      base.$copy = base.$el.clone();

      base.$copy
        .find('td:not(:first-child), th:not(:first-child)')
        .hide()
        .end()
        .find('caption')
        .html('&nbsp;');

      base.$el.closest('.sv-responsiveTable--fixedFirst').append(base.$copy);

      base.$copy.wrap(
        divStyleTmpl({
          classNames: classNames.fixedFirst + '__pinned',
        })
      );

      base.$el.wrap(
        divStyleTmpl({
          classNames: classNames.fixedFirst + '__scrollable',
        })
      );

      base.setCellHeights();
    };

    base.unSplitTable = function () {
      base.$el
        .closest(selectors.fixedFirst)
        .find(selectors.fixedFirst + '__pinned')
        .remove();
      base.$el.unwrap();
      base.$copy = undefined;
      base.unSetCellHeights();
    };

    base.setCellHeights = function () {
      var $tr = base.$el.find('tr'),
        $tr_copy = base.$copy.find('tr'),
        heights = [];

      $tr.each(function (i, el) {
        const $tx = $(el).find('th, td');

        $tx.each(function () {
          const height = $(this).outerHeight(true);

          heights[i] = heights[i] || 0;
          if (height > heights[i]) {
            heights[i] = height;
          }
        });
      });

      $tr_copy.each(function (i, el) {
        $(el).height(heights[i]);
        $tr.eq(i).height(heights[i]);
      });
    };

    base.unSetCellHeights = function () {
      base.$el.find('tr').each(function (i, el) {
        el.style.height = 'auto';
      });
    };

    base.updateTables = function () {
      if (!mql.matches && switched) {
        // Wider than config.breakpoint
        switched = false;
        base.unSplitTable();
      } else if (mql.matches && !switched) {
        switched = true;
        base.splitTable();
      }
    };

    mql.addListener(base.updateTables);
    $(base.updateTables);

    base.init();
  };

  $.fn.svRespTables_fixedFirst = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.fixedFirst(this);
      /*jshint nonew:true*/
    });
  };

  /**
   * stacktable.js
   * (Stacked table and stacked columns)
   *
   * Author & copyright (c) 2012: John Polacek
   * http://johnpolacek.github.io/stacktable.js/
   *
   * Dual MIT & GPL license
   *
   * modified by Henrik Ekelöf
   */

  //-Stacked Table-----------------------------------------------------------------------------//

  $.svRespTables.stackTable = function (el) {
    var base = this,
      mql = responsiveTables.mql;

    base.$el = $(el);

    base.$el.data('svRespTables.stackTable', base);

    base.init = function () {
      let markup = '';

      mql.removeListener(base.init);

      base.$copy = $(
        tblTmpl({
          classNames:
            base.$el.prop('class') +
            ' ' +
            classNames.stackGroupName +
            '__smallOnly',
        })
      );

      base.$el.addClass(classNames.stackGroupName + '__largeOnly');

      const $caption = base.$el.find('caption').clone();
      const $topRow = base.$el.find('tr').eq(0);

      base.$el.find('tr').each(function (rowIndex, tr) {
        var $tr = $(tr);
        let headMarkup = '';
        let bodyMarkup = '';
        const trClassName = $tr.prop('class');

        if (rowIndex === 0) {
          markup += tblThRowTmpl({
            trClassNames: trClassName,
            thClassNames:
              classNames.stackGroupName +
              '__headRow ' +
              classNames.stackGroupName +
              '__headRow--main',
            colSpan: 2,
            content: $tr.find('th, td').eq(0).html(),
          });
        } else {
          $tr.find('td, th').each(function (cellIndex, cell) {
            const $cell = $(cell);
            const cellHtml = $cell.html();
            const trimmedCell = cellHtml ? cellHtml.trim() : '';

            if (trimmedCell.length > 0) {
              if (cellIndex === 0) {
                headMarkup += tblThRowTmpl({
                  trClassNames: trClassName,
                  thClassNames: classNames.stackGroupName + '__headRow',
                  colSpan: 2,
                  content: cellHtml,
                });
              } else {
                const keyCellMarkup = $topRow
                  .find('td,th')
                  .eq(cellIndex)
                  .html();
                bodyMarkup += tblTrRowTmpl({
                  trClassNames: trClassName,
                  groupName: classNames.stackGroupName,
                  keyContent: keyCellMarkup ? keyCellMarkup.trim() : '',
                  cellClassName: $cell.prop('class'),
                  valueContent: cellHtml,
                });
              }
            }
          });
          markup += headMarkup + bodyMarkup;
        }
      });

      base.$copy.find('tbody').before($caption).html(markup);
      base.$el.before(base.$copy);
    };

    if (mql.matches) {
      base.init();
    } else {
      mql.addListener(base.init);
    }
  };

  $.fn.svRespTables_stackTable = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.stackTable(this);
      /*jshint nonew:true*/
    });
  };

  //-Stacked Columns---------------------------------------------------------------------------//

  $.svRespTables.stackColumns = function (el) {
    var base = this,
      mql = responsiveTables.mql;

    base.$el = $(el);

    base.$el.data('svRespTables.stackColumns', base);

    base.init = function () {
      var columnCount = base.$el.find('tr').eq(0).find('td, th').length,
        columnIndex = 1, //col index starts at 0 -> start copy at second column.
        $tbody;

      mql.removeListener(base.init);

      if (columnCount < 3) {
        return;
      }

      base.$copy = $(
        tblTmpl({
          classNames:
            base.$el.prop('class') +
            ' ' +
            classNames.stackGroupName +
            '__smallOnly',
        })
      );

      base.$el.addClass(classNames.stackGroupName + '__largeOnly');

      if (base.$el.find('caption').length) {
        base.$copy.find('tbody').before(base.$el.find('caption').clone());
      }

      $tbody = $('<tbody />');

      function makeTableRow(index, trEl) {
        var $tr = $(trEl),
          $newTr = $('<tr></tr>').addClass($tr.attr('class')),
          $firstCell,
          $secondCell,
          target,
          i;

        if (index === 0) {
          $newTr.addClass(
            classNames.stackGroupName +
              '__headRow ' +
              classNames.stackGroupName +
              '__headRow--main'
          );
        }

        $firstCell = $tr
          .find('td, th')
          .eq(0)
          .clone()
          .addClass(classNames.stackGroupName + '__key');

        target = columnIndex;

        // if colspan apply, recompute target for second cell.
        if ($tr.find('*[colspan]').length) {
          i = 0;
          $tr.find('td, th').each(function (j, colSpanEl) {
            var colspan = $(colSpanEl).attr('colspan');
            if (i > columnIndex) {
              //target is set; break.
              return false;
            }
            if (colspan) {
              colspan = parseInt(colspan, 10);
              target -= colspan - 1;
              if (i + colspan > columnIndex) {
                //out of current bounds
                target += i + colspan - columnIndex - 1;
              }
              i += colspan;
            } else {
              i++;
            }
          });
        }

        $secondCell = $tr.find('td, th').eq(target).clone();

        $secondCell
          .addClass(classNames.stackGroupName + '__value')
          .removeAttr('colspan');

        $newTr.append($firstCell, $secondCell);

        $tbody.append($newTr);
      }

      while (columnIndex < columnCount) {
        base.$el.find('tr').each(makeTableRow);
        ++columnIndex;
      }

      base.$copy.find('tbody').append($tbody.html());
      base.$el.before(base.$copy);
    };

    if (mql.matches) {
      base.init();
    } else {
      mql.addListener(base.init);
    }
  };

  $.fn.svRespTables_stackColumns = function () {
    return this.each(function () {
      /*jshint nonew:false*/
      new $.svRespTables.stackColumns(this);
      /*jshint nonew:true*/
    });
  };
})();

/**
 * SiteVision Responsive Tables Init
 */

responsiveTables.init = function () {
  const $responsiveTables = $(document.getElementsByTagName('table')).filter(
    selectors.groupName
  );
  if ($responsiveTables.length > 0) {
    $responsiveTables
      .filter(selectors.scroll)
      .svRespTables_scroll()
      .end()
      .filter(selectors.fixedFirst)
      .svRespTables_fixedFirst()
      .end()
      .filter(selectors.stackTable)
      .svRespTables_stackTable()
      .end()
      .filter(selectors.stackColumns)
      .svRespTables_stackColumns();
  }
};

$(function () {
  const $body = $(document.body);
  if ($body.hasClass('sv-responsive') && !$body.hasClass('sv-editing-mode')) {
    responsiveTables.mql = window.matchMedia(
      `(max-width: ${responsiveTables.config.breakpoint})`
    );
    responsiveTables.init();
  }
});
